import React, { useEffect, useState } from 'react';
import { aiAgentApi } from '../../../lib/aiAgentApi';
import { toast } from 'sonner';
import { FolderOpen, Upload, Trash2, Download, FileText } from 'lucide-react';

const fmtBytes = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};
const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

export const DriveView = () => {
  const [files, setFiles] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await aiAgentApi.listFiles();
      setFiles(r.files || []);
      setConfigured(!!r.configured);
    } catch { toast.error('Could not load files'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('File exceeds 25MB'); return; }
    setBusy(true);
    try {
      const pre = await aiAgentApi.presignUpload({
        filename: file.name, content_type: file.type || 'application/octet-stream',
        size_bytes: file.size, visibility: 'private',
      });
      const r = await fetch(pre.upload_url, { method: 'PUT', headers: { 'Content-Type': pre.content_type }, body: file });
      if (!r.ok) throw new Error(`Upload failed (${r.status})`);
      await aiAgentApi.confirmUpload({
        file_id: pre.file_id, s3_key: pre.s3_key, filename: file.name,
        content_type: file.type || 'application/octet-stream',
        size_bytes: file.size, visibility: 'private',
      });
      toast.success('Uploaded');
      await load();
    } catch (e) { toast.error(e?.message || 'Upload failed'); }
    finally { setBusy(false); }
  };

  const download = async (id, filename) => {
    try {
      const r = await aiAgentApi.downloadFile(id);
      window.open(r.download_url, '_blank');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not download'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await aiAgentApi.deleteFile(id);
      toast.success('Deleted');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); }
  };

  return (
    <div className="flex h-full flex-col" data-testid="ai-drive">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <FolderOpen className="h-4 w-4 text-emerald-600" /> Drive
        </div>
        <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 ${busy ? 'opacity-50 pointer-events-none' : ''}`}>
          <Upload className="h-3.5 w-3.5" /> {busy ? 'Uploading…' : 'Upload'}
          <input data-testid="ai-drive-upload" type="file" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
        </label>
      </div>

      {!configured && (
        <div className="m-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
          Drive storage is not configured on this environment. Ask your admin to set AWS keys & S3_BUCKET_NAME.
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-3 py-3">
        {loading && <div className="py-8 text-center text-xs text-slate-400">Loading…</div>}
        {!loading && files.length === 0 && (
          <div className="mt-10 text-center">
            <FolderOpen className="mx-auto h-8 w-8 text-slate-300" />
            <div className="mt-2 text-sm font-semibold text-slate-900">No files yet</div>
            <div className="mt-0.5 text-xs text-slate-500">Upload bills, statements, or spreadsheets — the agent can read them.</div>
          </div>
        )}
        <ul className="space-y-2">
          {files.map((f) => (
            <li key={f.id} data-testid={`ai-drive-file-${f.id}`} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 hover:border-emerald-200">
              <div className="flex min-w-0 items-center gap-2.5">
                <div className="rounded-lg bg-slate-100 p-2 text-slate-500"><FileText className="h-4 w-4" /></div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-slate-900">{f.filename}</div>
                  <div className="text-[11px] text-slate-500">{fmtBytes(f.size_bytes)} · {fmt(f.created_at)} · {f.visibility}</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => download(f.id, f.filename)} title="Download" className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"><Download className="h-4 w-4" /></button>
                <button onClick={() => remove(f.id)} title="Delete" className="rounded-md p-1.5 text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
